import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ad } from '@/api/types/common.types';
import { useToastStore } from '@/store/useToastStore';
import { useIosNudgeScrollOffTop } from '@/hooks/useIosNudgeScrollOffTop';

type ToastOptions = Parameters<
  ReturnType<typeof useToastStore.getState>['openToast']
>[0];

const TOAST_REOPEN_DELAY_MS = 50;

export function useBookmarkToasts() {
  const { t } = useTranslation();
  const openToast = useToastStore(state => state.openToast);
  const closeToast = useToastStore(state => state.closeToast);
  const onAnimationComplete = useToastStore(state => state.onAnimationComplete);
  const iosNudgeScrollOffTop = useIosNudgeScrollOffTop();
  const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);

  const replaceToast = useCallback(
    (options: ToastOptions) => {
      closeToast();
      onAnimationComplete();
      setTimeout(() => openToast(options), TOAST_REOPEN_DELAY_MS);
    },
    [closeToast, onAnimationComplete, openToast]
  );

  const openCollectionMaster = useCallback(() => {
    closeToast();
    iosNudgeScrollOffTop();
    setIsCollectionMasterOpen(true);
  }, [closeToast, iosNudgeScrollOffTop]);

  const closeCollectionMaster = useCallback(() => {
    setIsCollectionMasterOpen(false);
  }, []);

  const handleAddBookmark = useCallback(
    (ad: Ad) =>
      replaceToast({
        image: ad.photo?.url,
        text: t('bookmark_collections.toast_tip'),
        action: t('bookmark_collections.toast_action'),
        onActionClick: openCollectionMaster,
      }),
    [replaceToast, t, openCollectionMaster]
  );

  const handleRemoveBookmark = useCallback(
    (ad: Ad) =>
      replaceToast({
        image: ad.photo?.url,
        text: t('bookmark_collections.unbookmarked'),
      }),
    [replaceToast, t]
  );

  return {
    isCollectionMasterOpen,
    closeCollectionMaster,
    handleAddBookmark,
    handleRemoveBookmark,
  };
}
